import { ApexOptions } from "apexcharts";
import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GetDashboardAttendanceAnalyticsDataResponseType } from "../../types/attendance";

type Props = {
  data?: GetDashboardAttendanceAnalyticsDataResponseType["data"]["stats"];
};

enum Colors {
  WIFI = "#1DB469",
  LOCATION = "#4A72FF",
  REMOTE = "#FEA735",
  DEVICE = "#07DBFA",
  MANUAL = "#FF574D",
}

export default function AttendanceChart({ data }: Props) {
  const list = [
    {
      title: "WIFI",
      count: data?.wifiCheckinPercentage || 0,
      value: data?.wifiCheckins || 0,
      color: Colors.WIFI,
    },
    {
      title: "Location",
      count: data?.geoCheckinPercentage || 0,
      value: data?.geoCheckins || 0,
      color: Colors.LOCATION,
    },
    {
      title: "Remote",
      count: data?.remoteCheckinPercentage || 0,
      value: data?.remoteCheckins || 0,
      color: Colors.REMOTE,
    },
    {
      title: "Device",
      count: data?.deviceCheckinPercentage || 0,
      value: data?.deviceCheckins || 0,
      color: Colors.DEVICE,
    },
    {
      title: "Manual",
      count: data?.reconcileCheckinPercentage || 0,
      value: data?.reconcileCheckins || 0,
      color: Colors.MANUAL,
    },
  ];

  const allZero = list.every((item) => item.value === 0);

  const placeholderColor = "#E5E7EB";

  const series = allZero ? [100] : list.map((item) => item.value);
  const labels = allZero ? ["No Data"] : list.map((item) => item.title);
  const colors = allZero ? [placeholderColor] : list.map((item) => item.color);

  const [state] = useState<{
    series: number[];
    options: ApexOptions;
  }>({
    series,
    options: {
      chart: {
        type: "pie",
      },
      labels,
      legend: { show: false },
      stroke: {
        colors: ["#fff"],
        show: false,
      },
      fill: {
        opacity: 1,
      },
      colors,
      dataLabels: {
        enabled: !allZero,
        style: {
          fontSize: "12px",
          fontWeight: "light",
          colors: ["#fff"],
          fontFamily: "sans-serif",
        },
        formatter: function (_, opts) {
          return labels[opts.seriesIndex] || "";
        },
        dropShadow: {
          enabled: false,
        },
        offsetX: 0,
        offsetY: 1000,
      },
      yaxis: { show: false },
      states: {
        normal: {
          filter: {
            type: "none",
          },
        },
        hover: {
          filter: {
            type: allZero ? "none" : "lighten",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
      tooltip: {
        enabled: !allZero,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    },
  });

  return (
    <div className="grid xl:grid-cols-5 gap-4 h-full">
      <div className="xl:col-span-2 flex justify-center items-center w-full h-full pr-4">
        <ReactApexChart
          options={state.options}
          series={state.series}
          type="pie"
          width={230}
        />
      </div>

      <div className="col-span-3 h-full flex items-center w-full">
        <div className="w-full">
          {list.map((item) => (
            <div
              key={item.title}
              className="flex justify-between gap-2 items-center text-display-body text-base py-2 first:pt-0 last:pb-0 border-b border-extra-light last:border-b-0 w-full"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <h6 className="font-medium">{item.title}:</h6>
              </div>
              <h6 className="font-semibold">{item.count}%</h6>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
